// FIN-7 / H0-S4 — Dashboard admin de telemetria Pluggy.
//
// Lê /api/admin/pluggy (gated por ADMIN_USER_IDS no backend). Renderiza:
//   - KPIs: connections ativas, total events, errors, reconcile breakdown
//   - Tabela de events_by_type com count + avg latency + erros
//   - Latency percentiles (p50/p95) por event_type
//   - Lista dos últimos 20 erros
//
// Sem PII. Não exibe Client Secret nem tokens — apenas IDs opacos.

import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, RefreshCw, ArrowLeft, AlertCircle, Wifi,
} from "lucide-react";

import { authFetch } from "@/lib/authFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const RANGES = [
  { id: "1h", label: "Última hora" },
  { id: "24h", label: "24 horas" },
  { id: "7d", label: "7 dias" },
  { id: "30d", label: "30 dias" },
];

const fmtInt = (v) => Number(v || 0).toLocaleString("pt-BR");
const fmtMs = (v) => `${Number(v || 0).toLocaleString("pt-BR")} ms`;

function KpiCard({ label, value, hint, tone = "neutral" }) {
  const toneClasses = {
    neutral: "border-subtle bg-surface-card-hover",
    success: "border-status-success/30 bg-status-success/10",
    warning: "border-status-warning/30 bg-status-warning/10",
    error:   "border-status-error/30   bg-status-error/10",
  };
  return (
    <Card className={`p-4 ${toneClasses[tone]}`}>
      <div className="text-caption uppercase tracking-wider text-foreground-muted">
        {label}
      </div>
      <div className="text-h1 font-display font-bold text-foreground mt-1">
        {value}
      </div>
      {hint && <div className="text-caption text-foreground-muted mt-1">{hint}</div>}
    </Card>
  );
}

export default function AdminPluggyPage() {
  const navigate = useNavigate();
  const [range, setRange] = useState("24h");
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${API_URL}/api/admin/pluggy?range=${range}`);
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${r.status}`);
      }
      setData(await r.json());
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setIsLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const reconcile = data?.reconcile_summary || {};
  const totalEvents = (data?.events_by_type || []).reduce(
    (acc, e) => acc + (e.count || 0), 0,
  );
  const totalErrors = (data?.events_by_type || []).reduce(
    (acc, e) => acc + (e.errors || 0), 0,
  );

  return (
    <div className="min-h-screen bg-background text-foreground p-4 md:p-8">
      <div className="max-w-[1280px] mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Wifi className="w-5 h-5 text-brand-green" />
            <h1 className="font-display font-bold text-h1">Pluggy · Telemetria</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {RANGES.map((r) => (
              <Button
                key={r.id}
                size="sm"
                variant={range === r.id ? "default" : "outline"}
                onClick={() => setRange(r.id)}
              >
                {r.label}
              </Button>
            ))}
            <Button variant="outline" size="icon" onClick={fetchData} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {error && (
          <Card className="p-4 mb-6 border-status-error/40 bg-status-error/10 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-status-error shrink-0" />
            <div className="text-status-error text-body">{error}</div>
          </Card>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <KpiCard
            label="Conexões ativas"
            value={fmtInt(data?.active_connections)}
            tone="success"
          />
          <KpiCard
            label="Events"
            value={fmtInt(totalEvents)}
            hint={`Últimos ${range}`}
          />
          <KpiCard
            label="Erros"
            value={fmtInt(totalErrors)}
            tone={totalErrors > 0 ? "error" : "neutral"}
          />
          <KpiCard
            label="Match exato"
            value={fmtInt(reconcile.exact)}
            hint={`${fmtInt(reconcile.candidate)} candidates · ${fmtInt(reconcile.inserted)} novas`}
          />
        </div>

        {/* Events by type */}
        <Card className="p-4 md:p-6 mb-6">
          <h2 className="text-caption uppercase tracking-wider text-foreground-muted mb-3">
            Events por tipo
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pluggy-events-table">
              <thead>
                <tr className="text-left border-b border-subtle">
                  <th className="py-2 pr-3 text-foreground-muted font-normal">event_type</th>
                  <th className="py-2 pr-3 text-foreground-muted font-normal text-right">count</th>
                  <th className="py-2 pr-3 text-foreground-muted font-normal text-right">avg latency</th>
                  <th className="py-2 pr-3 text-foreground-muted font-normal text-right">p50 / p95</th>
                  <th className="py-2 pr-3 text-foreground-muted font-normal text-right">errors</th>
                </tr>
              </thead>
              <tbody>
                {(data?.events_by_type || []).map((e) => {
                  const pp = data?.latency_percentiles?.[e.event_type];
                  return (
                    <tr key={e.event_type} className="border-b border-subtle/50">
                      <td className="py-2 pr-3 font-mono text-foreground">{e.event_type}</td>
                      <td className="py-2 pr-3 text-right">{fmtInt(e.count)}</td>
                      <td className="py-2 pr-3 text-right text-foreground-muted">{fmtMs(e.avg_latency_ms)}</td>
                      <td className="py-2 pr-3 text-right text-foreground-muted">
                        {pp ? `${fmtMs(pp.p50_ms)} / ${fmtMs(pp.p95_ms)}` : "—"}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {e.errors > 0 ? (
                          <Badge className="bg-status-error/15 text-status-error border-status-error/40">
                            {e.errors}
                          </Badge>
                        ) : (
                          <span className="text-foreground-muted">0</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {(data?.events_by_type || []).length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-foreground-muted">
                      Sem events neste período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Recent errors */}
        <Card className="p-4 md:p-6">
          <h2 className="text-caption uppercase tracking-wider text-foreground-muted mb-3">
            Erros recentes
          </h2>
          {(data?.errors_recent || []).length === 0 ? (
            <div className="text-foreground-muted text-body py-2">Sem erros no período.</div>
          ) : (
            <div className="space-y-2">
              {(data?.errors_recent || []).map((e) => (
                <div
                  key={e.id}
                  className="rounded-lg border border-status-error/30 bg-status-error/5 p-3"
                >
                  <div className="flex items-center gap-2 text-caption">
                    <Badge className="bg-status-error/15 text-status-error border-status-error/40">
                      {e.event_type}
                    </Badge>
                    <span className="text-foreground-muted">{e.created_at}</span>
                    {e.item_id && (
                      <span className="font-mono text-foreground-muted">
                        item={String(e.item_id).slice(0, 12)}…
                      </span>
                    )}
                  </div>
                  <div className="text-body text-foreground mt-1 break-words">
                    {e.error || "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
